// biome-ignore-all lint/suspicious/noExplicitAny: The component is externally type-safe but making the implementation type-safe is not straightforward
import { useSelectionList } from "@agrigate/shared/lib/hooks/use-selection-list";
import type { KeyedEntity } from "@agrigate/shared/lib/utils/query";
import { toNum } from "@agrigate/ts-utils/lib/coercions";
import { formatDate, formatDateTime } from "@agrigate/ts-utils/lib/datetime";
import type { MaybeN, MaybeP, StrKey } from "@agrigate/ts-utils/lib/types";
import {
    Box,
    type ButtonProps,
    Card,
    type CardRootProps,
    Collapsible,
    DataList,
    Flex,
    Grid,
    GridItem,
    Heading,
    IconButton,
    Table,
    type TableColumnHeaderProps,
    type TableRootProps,
    Text,
} from "@chakra-ui/react";
import {
    type CSSProperties,
    Fragment,
    type ReactElement,
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { FaChevronDown, FaChevronRight, FaChevronUp } from "react-icons/fa6";
import { FiEdit, FiRefreshCcw } from "react-icons/fi";
import { HiOutlineTrash } from "react-icons/hi2";
import { LuChevronLeft, LuChevronRight } from "react-icons/lu";
import { StringParam, useQueryParam } from "use-query-params";
import {
    AccordionItem,
    AccordionItemContent,
    AccordionItemTrigger,
    AccordionRoot,
} from "./components/ui/accordion";
import { Checkbox } from "./components/ui/checkbox";
import { Switch } from "./components/ui/switch";
import DrawerCombo from "./DrawerCombo";
import FixedLoader from "./FixedLoader";
import { FlexSpc } from "./flex";
import { isDesktop } from "./hooks/use-is-desktop";
import PopoverMenuCombo from "./PopoverMenuCombo";

export type ColDataType = "string" | "number" | "date" | "datetime" | "boolean";

const { Body, Caption, Cell, ColumnHeader, Header, Root, Row, ScrollArea } =
    Table;

export interface DataTableColumnBase {
    key: string;
    label?: string;
    group?: string;
    getValue?: (row: any) => number;
    collapsedDisplay?: boolean;
    mergeKey?: (row: any) => string | number | undefined;
    width?: TableColumnHeaderProps["htmlWidth"];
    minWidth?: TableColumnHeaderProps["htmlWidth"];
    maxWidth?: TableColumnHeaderProps["htmlWidth"];
    dataType?: ColDataType;
    flexGrow?: number;
    flexShrink?: number;
    align?: "start" | "center" | "end";
    render?: (row: any, col: DataTableColumnBase, index?: number) => any;
}

export type ColDef<TItem extends object> =
    | {
          key: StrKey<TItem>;
          label?: string;
          group?: string;
          getValue?: (row: TItem) => number;
          collapsedDisplay?: boolean;
          mergeKey?: (row: TItem) => string | number | undefined;
          width?: TableColumnHeaderProps["htmlWidth"];
          minWidth?: TableColumnHeaderProps["htmlWidth"];
          maxWidth?: TableColumnHeaderProps["htmlWidth"];
          dataType?: ColDataType;
          align?: "start" | "center" | "end";
      }
    | {
          key: string;
          render: (
              row: TItem,
              col: DataTableColumnBase,
              index?: number,
          ) => ReactNode;
          label?: string;
          group?: string;
          getValue?: (row: TItem) => number;
          collapsedDisplay?: boolean;
          mergeKey?: (row: TItem) => string | number | undefined;
          width?: TableColumnHeaderProps["htmlWidth"];
          minWidth?: TableColumnHeaderProps["htmlWidth"];
          maxWidth?: TableColumnHeaderProps["htmlWidth"];
          flexGrow?: number;
          dataType?: ColDataType;
          align?: "start" | "center" | "end";
      };

type TItemWithIndex<T extends KeyedEntity> = T & { index: number };

export type AnyFormatter = Formatter<any, any>;

export const dateFormatter: AnyFormatter = d => {
    if (d instanceof Date || typeof d === "number") {
        return formatDate(d);
    }
    return d;
};

export const dateMonthFormatter: AnyFormatter = d => {
    if (d instanceof Date || typeof d === "number") {
        return formatDate(d, "dd-MMM");
    }
    return d;
};

export const dateTimeFormatter: AnyFormatter = d => {
    if (d instanceof Date || typeof d === "number") {
        const str = formatDateTime(d);
        const m = str?.match(/(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})(.*)/);
        if (m)
            return (
                <Flex dir="row" alignItems="center" whiteSpace="nowrap">
                    <Box>{m[1]}</Box>
                    <Box opacity={0.2} mx={1}>
                        T
                    </Box>
                    <Box>{m[2]}</Box>
                    <Box opacity={0.2} ml={1}>
                        {m[3]}
                    </Box>
                </Flex>
            );
        return str;
    }
    return d;
};

export type TableColumn<TItem> = {
    header: React.ReactNode;
    accessor: keyof TItem | string;
    cell?: (row: TItem) => ReactNode;
    align?: "start" | "center" | "end";
};

export interface DataTableColumn extends DataTableColumnBase {
    _type?: "column";
    fullText?: boolean;
}

export interface DataTableColumnGroup extends DataTableColumnBase {
    _type: "group";
    columns?: DataTableColumn[];
}

export type Formatter<TVal, TItem> = (val: TVal, row: TItem) => ReactNode;

export interface BulkAction {
    label: string;
    handler: (ids: string[]) => void;
}

interface RowActions<TItem extends KeyedEntity> {
    viewDetails?: (row: TItem) => void;
    viewEditor?: (row: TItem) => void;
    viewDeletePrompt?: (row: TItem) => void;
}

interface TableProps<TItem extends KeyedEntity> extends RowActions<TItem> {
    size?: "sm" | "md" | "lg";
    interactive?: boolean;
    bulkActions?: BulkAction[];
    getSummaryHeader?: (item: TItem) => string;
    qKey?: string;
    tableData: TItem[];
    columns: (DataTableColumn | DataTableColumnGroup)[];
    bordered?: boolean;
    height?: CardRootProps["h"];
    width?: CardRootProps["w"];
    headerHeight?: number;
    groupHeaderHeight?: number;
    getExpandedView?: (row?: TItem) => ReactNode;
    title?: string;
    formatters?: {
        [key in keyof TItem]?: Formatter<TItem[key], TItem>;
    };
    getRowStyle?: (row: TItem) => CSSProperties | undefined;
    onRowClick?: (row: TItem) => void;
    childComponents?: {
        headerControls?: (props: object) => React.ReactNode;
        detailsPanel?: (props: {
            id: string;
            entity?: MaybeN<TItem>;
            row?: MaybeN<TItem>;
            onClose?: () => void;
        }) => React.ReactNode;
        detailsDrawer?: (props: {
            id?: string;
            entity?: MaybeN<TItem>;
            row?: MaybeN<TItem>;
            isOpen: boolean;
            onClose: () => void;
        }) => React.ReactNode;
        // TODO Unify the above two
        filtersPanel?: (props: object) => React.ReactNode;
    };
    refetchInitial?: () => MaybeP<unknown>;
    fetchNext?: (pageNumber?: number) => MaybeP<unknown>;
    styles?: {
        container?: CSSProperties;
        body?: CSSProperties;
        cellContainerStyle?: CSSProperties;
        headerContainerStyle?: CSSProperties;
    };
    headerComponent?: ReactNode;
    footerComponent?: ReactNode;
    renderMode?: "table" | "accordion";
    renderEmpty?: () => ReactNode;
    isDataLoading?: boolean;
    disableFetchMore?: boolean;
    pageNo?: number;
    tableVariant?: TableRootProps["variant"];
}

// Chakra/Panda style props resolve bare numbers against the theme's `sizes`
// token scale (e.g. `80` -> `sizes.80` -> 20rem) instead of treating them as
// literal pixels, so numeric widths must be converted to px strings before
// being passed to width/minWidth/maxWidth style props.
const toCssSize = (
    value: TableColumnHeaderProps["htmlWidth"] | undefined,
): TableColumnHeaderProps["htmlWidth"] | undefined =>
    typeof value === "number" ? `${value}px` : value;

const getColumnWidths = (
    columns: (DataTableColumn | DataTableColumnGroup)[],
): (TableColumnHeaderProps["htmlWidth"] | undefined)[] => {
    const baseWidths = columns.map(col => col.width ?? col.minWidth);
    const totalFixedWidth = baseWidths.reduce<number>(
        (sum, w) => (typeof w === "number" ? sum + w : sum),
        0,
    );
    const totalFlexGrow = columns.reduce(
        (sum, col) => sum + (col.flexGrow ?? 0),
        0,
    );
    return columns.map((col, idx) => {
        if (baseWidths[idx] != null) return toCssSize(baseWidths[idx]);
        if (col.flexGrow && totalFlexGrow > 0) {
            return `calc((100% - ${totalFixedWidth}px) * ${col.flexGrow / totalFlexGrow})`;
        }
        return undefined;
    });
};

const getActionButtons = <TItem extends KeyedEntity>(
    actions: RowActions<TItem>,
    row: TItem,
) => {
    const buttons: ReactNode[] = [];
    if (actions.viewDetails) {
        buttons.push(
            <IconButton
                size={"xs"}
                variant="light"
                key="detailsAction"
                onClick={() => {
                    actions.viewDetails?.(row);
                }}
            >
                <FaChevronRight style={{ fontWeight: "bold" }} />
            </IconButton>,
        );
    }
    if (actions.viewEditor) {
        buttons.push(
            <IconButton
                size={"xs"}
                variant="light"
                key="viewAction"
                onClick={() => {
                    actions.viewEditor?.(row);
                }}
            >
                <FiEdit />
            </IconButton>,
        );
    }
    if (actions.viewDeletePrompt) {
        buttons.push(
            <IconButton
                size={"xs"}
                variant="light"
                key="deleteAction"
                onClick={() => {
                    actions.viewDeletePrompt?.(row);
                }}
            >
                <HiOutlineTrash />
            </IconButton>,
        );
    }
    return buttons;
};
const ChakraTable = <TItem extends KeyedEntity>({
    columns,
    tableData,
    size = "md",
    interactive = true,
    headerComponent,
    footerComponent,
    height,
    width,
    bulkActions,
    childComponents,
    formatters,
    getExpandedView,
    getSummaryHeader,
    getRowStyle,
    onRowClick,
    qKey,
    renderMode,
    title,
    fetchNext,
    refetchInitial,
    renderEmpty,
    styles,
    viewDeletePrompt,
    viewDetails,
    viewEditor,
    isDataLoading,
    disableFetchMore,
    pageNo,
    tableVariant,
}: TableProps<TItem>) => {
    const useDesktopLayout = useMemo(() => isDesktop(), []);

    const effectiveRenderMode =
        renderMode ?? (useDesktopLayout ? "table" : "accordion");

    const tableRef = useRef<HTMLTableElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const accordionRef = useRef<HTMLDivElement>(null);

    const idSelection = useSelectionList<string>();
    const [selectAll, setSelectAll] = useState(false);
    const [pageNumber, setPageNumber] = useState(pageNo ?? 1);
    const [enableLoadMore, setEnableLoadMore] = useState(true);

    const [expandedRowKeys, setExpandedRowKeys] = useState<Set<string>>(
        new Set(),
    );
    const [loading, setLoading] = useState(false);

    const indeterminate = selectAll ? !selectAll : !!idSelection.items.length;

    const data = useMemo((): TItemWithIndex<TItem>[] => {
        if (containerRef.current) {
            // Scroll back to previous position after loading more data
            containerRef.current.scrollTop = 0;
        }
        return (
            tableData?.map((item, index) => ({
                ...item,
                index,
            })) ?? []
        );
    }, [tableData]);
    const prevDisabled = pageNumber === 1;
    const handlePrev = () => {
        if (!prevDisabled) {
            fetchNext?.(pageNumber - 1);
            setPageNumber(pageNumber - 1);
        }
    };
    const handleNext = () => {
        if (enableLoadMore || !disableFetchMore) {
            fetchNext?.(pageNumber + 1);
            setPageNumber(pageNumber + 1);
        }
    };

    // useEffect(() => {
    //     if (!loading && pageNumber > 1) {
    //         if (dataLength === prevDataLength) {
    //             setEnableLoadMore(false);
    //         } else {
    //             setPrevDataLength(dataLength);
    //             setEnableLoadMore(true);
    //         }
    //     }
    // }, [loading, pageNumber]);

    useEffect(() => {
        if (data.length > 0 && idSelection.items.length === data.length) {
            setSelectAll(true);
        }
    }, [idSelection, data]);

    useEffect(() => {
        if (idSelection.items.length === 0) return;
        const visibleIds = new Set(data.map(d => d.id));
        const stillVisible = idSelection.items.filter(id => visibleIds.has(id));
        if (stillVisible.length !== idSelection.items.length) {
            idSelection.replaceItems(stillVisible);
            setSelectAll(false);
        }
    }, [data]);

    const [openId, setOpenId] = useQueryParam(
        `${qKey ?? "entity"}-id`,
        StringParam,
    );

    const actions = useMemo(
        (): RowActions<TItem> => ({
            viewEditor: viewEditor,
            viewDetails:
                viewDetails ??
                (childComponents?.detailsPanel || childComponents?.detailsDrawer
                    ? row => {
                          setOpenId(row.id);
                      }
                    : undefined),
            viewDeletePrompt: viewDeletePrompt,
        }),
        [viewEditor, viewDetails, viewDeletePrompt],
    );

    const hasActions = Object.values(actions).find(_ => !!_);

    const columnWidths = useMemo(() => getColumnWidths(columns), [columns]);

    const openRow = useMemo(() => {
        if (!openId) return null;
        return data.find(row => row.id === openId);
    }, [openId, data]);

    const openEntity = useMemo(() => {
        if (!openId) return null;
        return tableData?.find(e => e.id === openId);
    }, [tableData, openId]);

    const loadMore = async () => {
        if (fetchNext) {
            await fetchNext();
        }
    };

    const onScroll = useCallback(async () => {
        if (accordionRef.current) {
            const { scrollTop, scrollHeight, clientHeight } =
                accordionRef.current;
            const isNearBottom = scrollTop + clientHeight >= scrollHeight;

            if (isNearBottom) {
                if (!loading && enableLoadMore) {
                    setLoading(true);
                    setPageNumber(prev => prev + 1);
                    await loadMore();
                    setTimeout(() => {
                        setLoading(false);
                    }, 1000);
                }
            }
        }
    }, [enableLoadMore]);

    useEffect(() => {
        const listInnerElement = accordionRef.current;
        if (listInnerElement) {
            listInnerElement.addEventListener("scroll", onScroll);
            // Clean-up
            return () => {
                listInnerElement.removeEventListener("scroll", onScroll);
            };
        }
    }, [enableLoadMore]);

    const handleExpanded = (rowDataID: string) => {
        const newExpandedRowKeys = new Set(expandedRowKeys);
        if (newExpandedRowKeys.has(rowDataID)) {
            newExpandedRowKeys.delete(rowDataID);
        } else {
            newExpandedRowKeys.add(rowDataID);
        }
        setExpandedRowKeys(newExpandedRowKeys);
    };
    useEffect(() => {
        if (pageNo) setPageNumber(pageNo);
    }, [pageNo]);
    return (
        <Card.Root
            variant="subtle"
            h={height ?? `${500}px`}
            flex={!height ? 1 : undefined}
            style={styles?.container}
            background="white"
            w={width}
        >
            {(title || bulkActions || childComponents?.headerControls) && (
                <Card.Header
                    p={2}
                    bg="white"
                    display="flex"
                    flexDir="row"
                    alignItems="center"
                    gap={2}
                >
                    {title && (
                        <Heading size="sm" px={2}>
                            {refetchInitial && (
                                <IconButton
                                    variant={"light"}
                                    size="xs"
                                    mr={2}
                                    onClick={() => {
                                        refetchInitial();
                                        setPageNumber(1);
                                    }}
                                >
                                    <FiRefreshCcw height="14px" width="14px" />
                                </IconButton>
                            )}
                            {title}
                        </Heading>
                    )}
                    <FlexSpc />
                    {childComponents?.headerControls?.({})}
                    {bulkActions ? (
                        <Box>
                            <BulkActionsMenu
                                bulkActions={bulkActions}
                                ids={idSelection.items}
                                size="sm"
                                variant="outline"
                            />
                        </Box>
                    ) : null}
                </Card.Header>
            )}
            {headerComponent}
            {effectiveRenderMode === "table" ? (
                <ScrollArea
                    ref={containerRef}
                    // onScroll={handleScroll}
                    h={"100%"}
                >
                    <Root
                        ref={tableRef}
                        size={size}
                        interactive={interactive}
                        stickyHeader
                        fontSize={"xs"}
                        variant={tableVariant}
                        css={{
                            tableLayout: "fixed",
                            "& [data-sticky]": {
                                position: "sticky",
                                zIndex: 1,
                                bg: "bg",

                                _after: {
                                    content: '""',
                                    position: "absolute",
                                    pointerEvents: "none",
                                    top: "0",
                                    bottom: "-1px",
                                    width: "32px",
                                },
                            },

                            "& [data-sticky=end]": {
                                _after: {
                                    insetInlineEnd: "0",
                                    translate: "100% 0",
                                    shadow: "inset 8px 0px 8px -8px rgba(0, 0, 0, 0.16)",
                                },
                            },

                            "& [data-sticky=start]": {
                                _after: {
                                    insetInlineStart: "0",
                                    translate: "-100% 0",
                                    shadow: "inset -8px 0px 8px -8px rgba(0, 0, 0, 0.16)",
                                },
                            },
                            "& thead tr": {
                                shadow: "0 1px 0 0 {colors.border}",
                                "&:has(th[data-sticky])": {
                                    zIndex: 2,
                                },
                            },
                            "& tbody tr:hover": {
                                bg: "gray.50",
                                color: "inherit",
                            },
                        }}
                    >
                        <Header>
                            <Row>
                                {getExpandedView ? (
                                    <ColumnHeader>{"#"}</ColumnHeader>
                                ) : null}
                                {bulkActions?.length ? (
                                    <ColumnHeader>
                                        <Checkbox
                                            colorPalette={"brand"}
                                            aria-label="Select all rows"
                                            style={localStyles.checkboxStyle}
                                            checked={
                                                indeterminate
                                                    ? "indeterminate"
                                                    : selectAll
                                            }
                                            onCheckedChange={() => {
                                                if (
                                                    selectAll ||
                                                    indeterminate
                                                ) {
                                                    idSelection.clear();
                                                    setSelectAll(false);
                                                } else {
                                                    idSelection.clear();
                                                    data.forEach(item => {
                                                        idSelection.toggleItem(
                                                            item.id,
                                                        );
                                                    });
                                                    setSelectAll(true);
                                                }
                                            }}
                                        />
                                    </ColumnHeader>
                                ) : null}
                                {columns.map((col, idx) => (
                                    <ColumnHeader
                                        maxWidth={toCssSize(col.maxWidth)}
                                        minWidth={toCssSize(col.minWidth)}
                                        width={columnWidths[idx]}
                                        whiteSpace="normal"
                                        overflowWrap="anywhere"
                                        key={col.key}
                                    >
                                        {col.label}
                                    </ColumnHeader>
                                ))}
                                {hasActions ? (
                                    <ColumnHeader data-sticky="end" right={0}>
                                        {"Actions"}
                                    </ColumnHeader>
                                ) : null}
                            </Row>
                        </Header>
                        <Body>
                            {data.length
                                ? data.map(row => {
                                      const isExpanded = expandedRowKeys.has(
                                          row.id,
                                      );
                                      return (
                                          <Fragment key={row.id}>
                                              <Row
                                                  onClick={() =>
                                                      onRowClick?.(row)
                                                  }
                                                  key={row.id}
                                                  style={getRowStyle?.(row)}
                                              >
                                                  {getExpandedView ? (
                                                      <Cell>
                                                          {
                                                              <IconButton
                                                                  size={"xs"}
                                                                  variant={
                                                                      "ghost"
                                                                  }
                                                                  onClick={() => {
                                                                      handleExpanded(
                                                                          row.id,
                                                                      );
                                                                  }}
                                                              >
                                                                  {isExpanded ? (
                                                                      <FaChevronUp />
                                                                  ) : (
                                                                      <FaChevronDown />
                                                                  )}
                                                              </IconButton>
                                                          }
                                                      </Cell>
                                                  ) : null}
                                                  {bulkActions?.length ? (
                                                      <Cell>
                                                          <Checkbox
                                                              colorPalette={
                                                                  "brand"
                                                              }
                                                              style={
                                                                  localStyles.checkboxStyle
                                                              }
                                                              checked={idSelection.isSelected(
                                                                  row.id,
                                                              )}
                                                              onCheckedChange={() => {
                                                                  idSelection.toggleItem(
                                                                      row.id,
                                                                  );
                                                              }}
                                                          />
                                                      </Cell>
                                                  ) : null}
                                                  {columns.map((col, idx) => {
                                                      const cellContent =
                                                          (() => {
                                                              const key =
                                                                  col.key as keyof TItem;
                                                              const formatter =
                                                                  formatters?.[
                                                                      key
                                                                  ];
                                                              const rowValue = (
                                                                  row as any
                                                              )[col.key];

                                                              if (
                                                                  key ===
                                                                  "index"
                                                              ) {
                                                                  return (
                                                                      rowValue +
                                                                      1
                                                                  );
                                                              }
                                                              if (formatter) {
                                                                  return (
                                                                      formatter(
                                                                          rowValue,
                                                                          row as TItem,
                                                                      ) ?? "-"
                                                                  );
                                                              }
                                                              if (
                                                                  col?.dataType ===
                                                                  "boolean"
                                                              ) {
                                                                  return (
                                                                      <Switch
                                                                          checked={
                                                                              !!rowValue
                                                                          }
                                                                      />
                                                                  );
                                                              }
                                                              if (col.render) {
                                                                  return col.render(
                                                                      row,
                                                                      col,
                                                                      idx,
                                                                  );
                                                              }
                                                              return (
                                                                  rowValue ??
                                                                  "-"
                                                              );
                                                          })();
                                                      return (
                                                          <Cell
                                                              key={`${row.id}-${col.key ?? idx}`}
                                                              textAlign={
                                                                  col.align
                                                              }
                                                              maxWidth={toCssSize(
                                                                  col.maxWidth,
                                                              )}
                                                              minWidth={toCssSize(
                                                                  col.minWidth,
                                                              )}
                                                              width={
                                                                  columnWidths[
                                                                      idx
                                                                  ]
                                                              }
                                                              whiteSpace="normal"
                                                              overflowWrap="anywhere"
                                                              style={{
                                                                  ...styles?.cellContainerStyle,
                                                              }}
                                                          >
                                                              {cellContent}
                                                          </Cell>
                                                      );
                                                  })}
                                                  {hasActions ? (
                                                      <Cell
                                                          data-sticky="end"
                                                          right={0}
                                                      >
                                                          {getActionButtons(
                                                              actions,
                                                              row,
                                                          )}
                                                      </Cell>
                                                  ) : null}
                                              </Row>
                                              {isExpanded &&
                                                  getExpandedView && (
                                                      <Row>
                                                          <Cell
                                                              colSpan={
                                                                  columns.length +
                                                                  1
                                                              }
                                                              p={0}
                                                              border="none"
                                                          >
                                                              <Collapsible.Root
                                                                  open={
                                                                      isExpanded
                                                                  }
                                                              >
                                                                  <Collapsible.Trigger paddingY="3">
                                                                      <Collapsible.Indicator
                                                                          transition="transform 0.2s"
                                                                          _open={{
                                                                              transform:
                                                                                  "rotate(90deg)",
                                                                          }}
                                                                      >
                                                                          <LuChevronRight />
                                                                      </Collapsible.Indicator>
                                                                  </Collapsible.Trigger>
                                                                  <Collapsible.Content>
                                                                      {getExpandedView(
                                                                          row,
                                                                      )}
                                                                  </Collapsible.Content>
                                                              </Collapsible.Root>
                                                          </Cell>
                                                      </Row>
                                                  )}
                                          </Fragment>
                                      );
                                  })
                                : null}
                        </Body>
                        <Caption>
                            {!isDataLoading && !data.length ? (
                                renderEmpty && !isDataLoading ? (
                                    renderEmpty()
                                ) : (
                                    <Flex
                                        align={"center"}
                                        height={"100%"}
                                        justify={"center"}
                                        p={10}
                                    >
                                        <Text
                                            fontWeight={"500"}
                                            color={"iceGrey.500"}
                                        >
                                            {"No Data Found"}
                                        </Text>
                                    </Flex>
                                )
                            ) : null}
                        </Caption>
                    </Root>
                    {fetchNext &&
                    data.length &&
                    !(pageNumber === 1 && disableFetchMore) ? (
                        <Flex
                            p={2}
                            justify="flex-start"
                            align="center"
                            gap={3}
                            mt={"auto"}
                        >
                            <IconButton
                                aria-label="Previous page"
                                onClick={handlePrev}
                                disabled={prevDisabled}
                                variant={"plain"}
                            >
                                {<LuChevronLeft />}
                            </IconButton>
                            <Text
                                fontWeight="bold"
                                minWidth="24px"
                                textAlign="center"
                            >
                                {pageNumber}
                            </Text>
                            <IconButton
                                aria-label="Next page"
                                onClick={handleNext}
                                disabled={!enableLoadMore || disableFetchMore}
                                variant={"plain"}
                            >
                                {<LuChevronRight />}
                            </IconButton>
                        </Flex>
                    ) : null}
                </ScrollArea>
            ) : (
                <AccordionRoot
                    maxHeight={"600px"}
                    overflowY={"scroll"}
                    ref={accordionRef}
                    collapsible
                >
                    {data.length > 0 ? (
                        data.map((row: any, index): ReactElement => {
                            const actionData = viewDetails ? (
                                <Box>
                                    <FaChevronRight
                                        style={{ fontWeight: "bold" }}
                                        onClick={() => {
                                            viewDetails?.(row);
                                        }}
                                    />
                                </Box>
                            ) : null;
                            return (
                                <AccordionItem
                                    key={`${row.id}-${index}`}
                                    p={0}
                                    value={index.toString()}
                                >
                                    <h2>
                                        <AccordionItemTrigger
                                            style={{
                                                display: "flex",
                                                flexDirection: "row",
                                                justifyContent: "space-between",
                                            }}
                                            indicatorPlacement="start"
                                        >
                                            <Box
                                                textAlign="left"
                                                px={2}
                                                flexGrow={1}
                                                flexShrink={1}
                                                textOverflow="ellipsis"
                                                overflow="hidden"
                                                whiteSpace="nowrap"
                                            >
                                                {getSummaryHeader?.(row) ??
                                                    row.id}
                                            </Box>
                                            {actionData}
                                        </AccordionItemTrigger>
                                    </h2>
                                    <AccordionItemContent
                                        style={{
                                            padding: 0,
                                        }}
                                    >
                                        <DataList.Root
                                            orientation="horizontal"
                                            divideY="1px"
                                        >
                                            {columns.map(
                                                (col, index, array) => {
                                                    const key =
                                                        col.key as keyof TItem;
                                                    const { label } =
                                                        col as DataTableColumn;
                                                    const cKey =
                                                        col.key as keyof TItem;
                                                    const formatter =
                                                        formatters?.[cKey];
                                                    let data: any;
                                                    if (key === "index") {
                                                        data =
                                                            toNum(row[key]) + 1;
                                                    } else if (formatter) {
                                                        data =
                                                            formatter(
                                                                row[key],
                                                                row,
                                                            ) ?? "-";
                                                    } else if (
                                                        col?.dataType ===
                                                        "boolean"
                                                    ) {
                                                        data = (
                                                            <Switch
                                                                checked={
                                                                    !!row[key]
                                                                }
                                                            />
                                                        );
                                                    } else if (
                                                        (col as DataTableColumn)
                                                            .render
                                                    ) {
                                                        data = (
                                                            col as DataTableColumn
                                                        ).render!(
                                                            row,
                                                            col as DataTableColumn,
                                                            index,
                                                        );
                                                    } else {
                                                        data = row[key] ?? "-";
                                                    }
                                                    return (
                                                        <DataList.Item
                                                            style={{
                                                                width: "100%",
                                                                paddingLeft: 10,
                                                            }}
                                                            pt={4}
                                                            pb={
                                                                index ===
                                                                array.length - 1
                                                                    ? 4
                                                                    : undefined
                                                            }
                                                            key={`${row.id}-${index}`}
                                                        >
                                                            <DataList.ItemLabel
                                                                style={{
                                                                    width: "40%",
                                                                    fontWeight:
                                                                        "bold",
                                                                }}
                                                            >
                                                                {label}:
                                                            </DataList.ItemLabel>
                                                            <DataList.ItemValue
                                                                style={{
                                                                    width: "60%",
                                                                }}
                                                            >
                                                                {data}
                                                            </DataList.ItemValue>
                                                            {hasActions &&
                                                            effectiveRenderMode !==
                                                                "accordion" ? (
                                                                <Grid
                                                                    style={{
                                                                        width: "100%",
                                                                    }}
                                                                >
                                                                    <GridItem
                                                                        style={{
                                                                            width: "100%",
                                                                        }}
                                                                    >
                                                                        {getActionButtons(
                                                                            actions,
                                                                            row,
                                                                        )}
                                                                    </GridItem>
                                                                </Grid>
                                                            ) : null}
                                                        </DataList.Item>
                                                    );
                                                },
                                            )}
                                        </DataList.Root>
                                    </AccordionItemContent>
                                </AccordionItem>
                            );
                        })
                    ) : (
                        <Box p={10}>
                            {!isDataLoading &&
                                (renderEmpty ? (
                                    renderEmpty()
                                ) : (
                                    <Flex
                                        align={"center"}
                                        justify={"center"}
                                        direction="column"
                                    >
                                        <Text
                                            fontWeight={"500"}
                                            color={"iceGrey.500"}
                                        >
                                            {"No Data Found"}
                                        </Text>
                                    </Flex>
                                ))}
                        </Box>
                    )}
                </AccordionRoot>
            )}

            {footerComponent}

            {childComponents?.detailsDrawer?.({
                id: openId ?? undefined,
                isOpen: !!openId,
                onClose: () => setOpenId(null),
                entity: openEntity,
                row: openRow,
            })}

            {childComponents?.detailsPanel && (
                <DrawerCombo
                    isOpen={!!openId}
                    onClose={() => {
                        setOpenId(null);
                    }}
                    size={"lg"}
                    title="Details"
                >
                    {openId
                        ? childComponents?.detailsPanel?.({
                              id: openId,
                              row: openRow,
                              entity: openEntity,
                              onClose: () => setOpenId(null),
                          })
                        : null}
                </DrawerCombo>
            )}

            {(loading || isDataLoading) && <FixedLoader />}

            {/* <ActionBar.Root open={hasSelection}>
                <Portal>
                    <ActionBar.Positioner>
                        <ActionBar.Content>
                            <ActionBar.SelectionTrigger>
                                {idSelection.items.length} selected
                            </ActionBar.SelectionTrigger>
                            <ActionBar.Separator />
                            {bulkActions?.map(action => {
                                return (
                                    <Button
                                        variant={"light"}
                                        onClick={() =>
                                            action.handler(idSelection.items)
                                        }
                                    >
                                        {action.label}
                                    </Button>
                                );
                            })}
                            <ActionBar.CloseTrigger asChild>
                                <CloseButton
                                    onClick={() => idSelection.clear()}
                                    size="sm"
                                />
                            </ActionBar.CloseTrigger>
                        </ActionBar.Content>
                    </ActionBar.Positioner>
                </Portal>
            </ActionBar.Root> */}
        </Card.Root>
    );
};

export default ChakraTable;

export function BulkActionsMenu(p: {
    ids: string[];
    bulkActions: BulkAction[];
    onPreHandle?: () => void;
    onPostHandle?: () => void;
    size?: ButtonProps["size"];
    variant?: ButtonProps["variant"];
    colorPalette?: ButtonProps["colorPalette"];
}): ReactElement {
    const options = useMemo(
        () =>
            p.bulkActions.map(action => ({
                type: "item" as const,
                key: action.label,
                label: action.label,
                onClick: () => action.handler(p.ids),
            })),
        [p.bulkActions, p.ids],
    );

    return (
        <PopoverMenuCombo
            label="Bulk Actions"
            options={options}
            disabled={!p.ids.length}
            size={p.size}
            variant={p.variant ?? "infoLight"}
            colorPalette={p.colorPalette}
        />
    );
}
const localStyles: Record<string, CSSProperties> = {
    cellContentStyle: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
    },
    checkboxStyle: {
        borderColor: "#121924",
    },
};
